import React, { useContext, useEffect } from "react";

import LocalLogicClient from "@local-logic/client";
import { setCSSVariables } from "./colors";
import { ProductNames } from "~/types";
import type {
  Environment,
  Appearance,
  Locale,
  ExternalId,
} from "~/types/options";

interface LocalLogicProviderProps {
  apiKey?: string;
  externalId?: string;
  appearance?: Appearance;
  children: React.ReactNode;
  locale?: Locale;
  product?: ProductNames;
  environment?: Environment["api"];
}

interface DefaultContext {
  client: ReturnType<typeof LocalLogicClient>;
  locale: Locale;
  options: {
    externalId?: ExternalId;
    appearance?: Appearance;
  };
}

const LocalLogicContext = React.createContext<DefaultContext>(
  {} as DefaultContext,
);

const useLocalLogic = () => useContext(LocalLogicContext);

const LocalLogicProvider = ({
  appearance,
  apiKey,
  externalId,
  children,
  locale,
  product,
  environment,
}: LocalLogicProviderProps) => {
  useEffect(() => {
    /**
     * Sets the major theme.
     */
    if (appearance?.theme) {
      document.documentElement.setAttribute("data-theme", appearance?.theme);
    }

    /**
     * Allows clients to override CSS theme variables.
     */
    setCSSVariables(appearance?.variables);
  }, [appearance?.theme, appearance?.variables]);

  if (!apiKey) {
    throw Error("A valid apiKey must be provided.");
  }

  const client = LocalLogicClient(apiKey, {
    locale,
    product,
    environment,
  });

  return (
    <LocalLogicContext.Provider
      value={{
        client,
        locale,
        options: {
          externalId,
          appearance,
        },
      }}
    >
      {children}
    </LocalLogicContext.Provider>
  );
};

export { useLocalLogic, LocalLogicProvider, LocalLogicContext };
